// src/lib/templateSync.js
import { db } from "@/firebase";
import { doc, getDoc, getDocFromServer, setDoc, runTransaction, serverTimestamp } from "firebase/firestore";
import { formatDateKey, addWeeksToDateKey } from "@/lib/dateUtils";
import { saveDogs, sanitizeForFirestore } from "@/realtimeDogs";
import { getDogIdentity, normalizeDogName } from "@/lib/dogIdentity";
import { resolveNoteForDate, isProtectedNoteException } from "@/lib/noteRules";


/**
 * Helper para mergear perros por IDENTIDAD ESTABLE (id, o nombre normalizado
 * como fallback legacy) sin duplicar.
 *
 * Prioridad ABSOLUTA: si el perro ya existe en remoteDogs, remoto SIEMPRE
 * gana — la plantilla nunca sobrescribe ni duplica un registro remoto ya
 * materializado en Firestore. La plantilla solo APORTA perros que remoto
 * todavía no tiene (semana futura aún no creada).
 *
 * Solo se consideran perros de plantilla con isFixed !== false: si un
 * cliente dejó de ser fijo, no debe seguir alimentando semanas futuras.
 *
 * Al materializar un cliente NUEVO (presente en templateDogs pero ausente en
 * remoteDogs) se resuelve `informativeNote` para el `dayKey` recibido a
 * partir de `recurringNotes` (histórico versionado, ya propagado a la
 * plantilla vía upsertDogInTemplate), y se fija `informativeNoteOverride:
 * false` explícitamente — el nuevo día nace bajo régimen recurrente, nunca
 * con una excepción puntual protegida. No se crean días, clientes ni listas
 * parciales: esto solo decide los valores del propio registro que ya se
 * estaba a punto de insertar.
 *
 * @param {Array} remoteDogs
 * @param {Array} templateDogs
 * @param {string} [dayKey] - "YYYY-MM-DD" del día que se está materializando
 */
const mergeByIdentity = (remoteDogs = [], templateDogs = [], dayKey) => {
  const map = new Map(remoteDogs.map(d => [getDogIdentity(d), d]));
  for (const d of templateDogs) {
    if (d.isFixed === false) continue; // ya no es fijo: no propagar a futuro
    const identity = getDogIdentity(d);
    if (!map.has(identity)) {
      const materialized = dayKey
        ? {
            ...d,
            informativeNote: resolveNoteForDate(d.recurringNotes, dayKey),
            informativeNoteOverride: false,
          }
        : d;
      map.set(identity, materialized);
    }
  }
  return Array.from(map.values());
};


/**
 * Busca la entrada de plantilla correspondiente a una identidad dada.
 * Prioridad: id exacto > importKey exacto > nombre normalizado (legacy).
 */
const findTemplateEntry = (templateDogs, { id, importKey, name } = {}) => {
  if (!Array.isArray(templateDogs)) return undefined;
  if (id) {
    const byId = templateDogs.find(d => d.id === id);
    if (byId) return byId;
  }
  if (importKey) {
    const byImportKey = templateDogs.find(d => d.importKey === importKey);
    if (byImportKey) return byImportKey;
  }
  if (name) {
    const normalized = normalizeDogName(name);
    return templateDogs.find(
      d => !d.id && !d.importKey && normalizeDogName(d.name) === normalized
    );
  }
  return undefined;
};

/**
 * Fase 2 integridad — Todas las escrituras a templates/{profile} pasan por
 * runTransaction. Esto elimina la carrera de "leer viejo, escribir encima"
 * que ocurría cuando dos operaciones (p.ej. rename + reorder, o dos pestañas)
 * llamaban getTemplate() + setDoc() en paralelo: cada una leía un estado
 * potencialmente desactualizado y su escritura podía pisar la de la otra
 * (nombre antiguo, orden antiguo resucitados). runTransaction garantiza que
 * la lectura y la escritura ocurren atómicamente contra el estado real más
 * reciente del documento, con reintento automático de Firestore si hay
 * conflicto de versión.
 *
 * `mutator(currentData)` recibe los datos actuales del documento
 * ({ templateDogs, windowEndDayKey, createdFrom } o null si no existe) y
 * debe devolver los datos completos a escribir, o `null` para no escribir
 * nada (no-op).
 */
const runTemplateTransaction = async (workspaceId, profile, mutator) => {
  const templateRef = doc(db, `workspaces/${workspaceId}/templates/${profile}`);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(templateRef);
    const current = snap.exists() ? snap.data() : null;
    const next = mutator(current);
    if (!next) return null; // no-op: nada que cambiar
    tx.set(templateRef, next, { merge: true });
    return next;
  });
};

/**
 * Guarda la plantilla de perros para un perfil
 * @param {string} workspaceId
 * @param {string} profile - Nombre del perfil (ej: "David", "Sara")
 * @param {Array} dogs - Lista de perros normalizados
 * @param {string} windowEndDayKey - Fecha límite de la ventana (formato YYYY-MM-DD)
 */
export const saveTemplate = async (workspaceId, profile, dogs, windowEndDayKey) => {
  try {
    const templateRef = doc(db, `workspaces/${workspaceId}/templates/${profile}`);
    
    const templateData = {
      templateDogs: dogs || [],
      windowEndDayKey,
      updatedAt: new Date().toISOString(),
      createdFrom: "pdf"
    };

    await setDoc(templateRef, templateData);
    
    console.error('[TEMPLATE_SAVE]', JSON.stringify({
      profile,
      dogsLen: dogs?.length || 0,
      windowEndDayKey
    }));
    
    return templateData;
  } catch (e) {
    console.error('[TEMPLATE_SAVE_ERROR]', JSON.stringify({
      profile,
      message: e?.message || String(e)
    }));
    throw e;
  }
};

/**
 * Obtiene la plantilla de perros para un perfil
 *
 * Por defecto usa `getDoc` (puede servir desde caché local de Firestore).
 * Cuando `options.source === "server"`, la lectura se hace EXCLUSIVAMENTE
 * con `getDocFromServer`, ignorando cualquier copia cacheada. Esto es
 * obligatorio para la importación de PDF: una plantilla cacheada obsoleta
 * podría resucitar nombres editados antiguos aunque el documento ya no
 * exista en el servidor (borrado manual en Firebase Console, por ejemplo).
 *
 * Si `options.source === "server"` y la lectura contra servidor falla por
 * red/permisos, con `options.throwOnError === true` el error se relanza
 * (no se hace fallback silencioso a `null` ni a caché). Si el documento
 * simplemente no existe en servidor, se devuelve `null` (plantilla vacía
 * real, comportamiento esperado).
 *
 * @param {string} workspaceId
 * @param {string} profile
 * @param {{ source?: "cache-first" | "server", throwOnError?: boolean }} [options]
 * @returns {Promise<{templateDogs: Array, windowEndDayKey: string} | null>}
 */
export const getTemplate = async (workspaceId, profile, options = {}) => {
  const { source = "cache-first", throwOnError = false } = options;
  const templateRef = doc(db, `workspaces/${workspaceId}/templates/${profile}`);

  if (source === "server") {
    try {
      const snap = await getDocFromServer(templateRef);
      if (!snap.exists()) {
        return null; // plantilla vacía real confirmada por servidor
      }
      return snap.data();
    } catch (e) {
      console.error('[TEMPLATE_GET_SERVER_ERROR]', JSON.stringify({
        profile,
        message: e?.message || String(e)
      }));
      if (throwOnError) {
        throw e; // nunca fallback silencioso a caché
      }
      return null;
    }
  }

  try {
    const snap = await getDoc(templateRef);
    
    if (!snap.exists()) {
      return null;
    }
    
    return snap.data();
  } catch (e) {
    console.error('[TEMPLATE_GET_ERROR]', JSON.stringify({
      profile,
      message: e?.message || String(e)
    }));
    return null;
  }
};


/**
 * Elimina un perro de la plantilla del perfil, localizándolo por identidad
 * estable (id si se provee, si no por nombre normalizado — comportamiento
 * legacy). Previene que extendTemplateWindow lo recree en semanas futuras.
 *
 * Usa runTransaction (ver runTemplateTransaction) para evitar carreras con
 * otras escrituras concurrentes de plantilla (rename, upsert, reorder).
 *
 * @param {string} workspaceId
 * @param {string} profile
 * @param {string} dogName - nombre (usado como fallback/legacy)
 * @param {string} [dogId] - id estable, si se conoce (prioridad absoluta)
 */
export const removeFromTemplate = async (workspaceId, profile, dogName, dogId) => {
  try {
    const normalizedName = (dogName || "").trim().toUpperCase();
    const result = await runTemplateTransaction(workspaceId, profile, (current) => {
      if (!current || !Array.isArray(current.templateDogs)) return null;
      const newDogs = current.templateDogs.filter(d => {
        if (dogId) return d.id !== dogId;
        return (d.name || "").trim().toUpperCase() !== normalizedName;
      });
      if (newDogs.length === current.templateDogs.length) return null; // no-op
      return {
        ...current,
        templateDogs: newDogs,
        updatedAt: new Date().toISOString(),
      };
    });
    if (result) {
      console.error('[REMOVE_FROM_TEMPLATE_OK]', JSON.stringify({ profile, dogName, byId: !!dogId }));
    }
  } catch (e) {
    console.error('[REMOVE_FROM_TEMPLATE_ERR]', e?.message);
  }
};

/**
 * Fase 2 — Helper ÚNICO para insertar/actualizar un cliente en templateDogs
 * por IDENTIDAD (id si existe, si no importKey, si no nombre normalizado
 * legacy). Debe ser el único punto de escritura de templateDogs para:
 *   - alta manual de un cliente fijo
 *   - edición de un cliente
 *   - reordenación de clientes (actualiza originalOrder)
 *   - importación PDF (resolución de importKey → id existente)
 *
 * Actualiza todos los campos relevantes: name, importKey, originalOrder,
 * whatsappGroup, locationUrl, schedules, count, isFixed.
 *
 * Si el cliente ya no es fijo (isFixed === false), se mantiene en la
 * plantilla con ese flag para que mergeByIdentity lo excluya de la
 * propagación a semanas futuras (no se borra, solo deja de propagarse).
 *
 * Usa runTransaction: sin esto, un rename y un upsert (o dos upserts)
 * casi simultáneos podían leer el mismo templateDogs desactualizado y
 * el último en escribir "ganaba" con datos parciales, resucitando un
 * campo antiguo (nombre, orden) que el otro ya había actualizado.
 *
 * @param {string} workspaceId
 * @param {string} profile
 * @param {object} dogData - datos completos del perro (debe incluir id o name)
 * @returns {Promise<void>}
 */
export const upsertDogInTemplate = async (workspaceId, profile, dogData) => {
  try {
    if (!dogData || (!dogData.id && !dogData.name)) return;

    let resultInfo = null;

    await runTemplateTransaction(workspaceId, profile, (current) => {
      const existingDogs = Array.isArray(current?.templateDogs) ? current.templateDogs : [];

      const existing = findTemplateEntry(existingDogs, {
        id: dogData.id,
        importKey: dogData.importKey,
        name: dogData.name,
      });

      const mergedEntry = {
        ...(existing || {}),
        ...dogData,
        // Preservar importKey original si dogData no trae uno explícito
        importKey: dogData.importKey ?? existing?.importKey,
        // Preservar originalOrder existente si dogData no especifica uno
        originalOrder: dogData.originalOrder ?? existing?.originalOrder ?? Date.now(),
        // Propagar recurringNotes explícitamente: es dato de identidad
        // (histórico de reglas recurrentes), igual que importKey/
        // originalOrder. Sin este fallback explícito, un dogData que no
        // incluya la clave (undefined) dejaría intacto el valor de
        // `existing` gracias al spread, pero si algún caller llegara a
        // pasar `recurringNotes: undefined` explícitamente, el spread de
        // `...dogData` lo sobrescribiría perdiendo el histórico ya
        // guardado en la plantilla. Este fallback ?? cubre ambos casos.
        recurringNotes: dogData.recurringNotes ?? existing?.recurringNotes ?? [],
      };


      let newDogs;
      if (existing) {
        newDogs = existingDogs.map(d => (d === existing ? mergedEntry : d));
      } else {
        newDogs = [...existingDogs, mergedEntry];
      }

      resultInfo = { id: mergedEntry.id, name: mergedEntry.name, wasExisting: !!existing };

      return {
        templateDogs: newDogs,
        windowEndDayKey: current?.windowEndDayKey || null,
        updatedAt: new Date().toISOString(),
        createdFrom: current?.createdFrom || "manual",
      };
    });

    if (resultInfo) {
      console.error('[UPSERT_TEMPLATE_OK]', JSON.stringify({ profile, ...resultInfo }));
    }
  } catch (e) {
    console.error('[UPSERT_TEMPLATE_ERR]', e?.message);
  }
};

/**
 * Elimina un perro de la plantilla por identidad completa (id/importKey/name)
 * y actualiza originalOrder del resto según una lista de identidades en orden
 * (usado en reordenación y borrado total).
 */
export const removeDogFromTemplateByIdentity = async (workspaceId, profile, dogData) => {
  return removeFromTemplate(workspaceId, profile, dogData?.name, dogData?.id);
};

/**
 * Fase 2 — Actualiza originalOrder en la plantilla para todos los clientes
 * fijos afectados por una reordenación. Localiza cada entrada por identidad
 * (id si existe, si no nombre normalizado). Si un cliente fijo reordenado
 * todavía no existe en la plantilla (ej. dado de alta antes de esta fase),
 * se crea la entrada para que quede correctamente posicionado de ahora en
 * adelante. Los clientes con isFixed === false se ignoran (no deben seguir
 * alimentando semanas futuras desde la plantilla).
 *
 * Usa runTransaction: evita que esta actualización de orden pise (o sea
 * pisada por) un rename/upsert concurrente sobre el mismo documento de
 * plantilla — antes cada uno hacía su propio getTemplate()+setDoc() por
 * separado, sin ninguna garantía de atomicidad entre ambos.
 *
 * @param {string} workspaceId
 * @param {string} profile
 * @param {Array} orderedDogs - lista de perros en el ORDEN final deseado
 */
export const updateTemplateOrder = async (workspaceId, profile, orderedDogs = []) => {
  try {
    if (!Array.isArray(orderedDogs) || orderedDogs.length === 0) return;

    let changedCount = 0;

    await runTemplateTransaction(workspaceId, profile, (current) => {
      const existingDogs = Array.isArray(current?.templateDogs) ? current.templateDogs : [];
      const byIdentity = new Map(existingDogs.map(d => [getDogIdentity(d), d]));
      let changed = false;

      orderedDogs.forEach((dog, index) => {
        if (dog.isFixed === false) return; // ya no es fijo: no alimenta plantilla
        const identity = getDogIdentity(dog);
        const existing = byIdentity.get(identity);
        if (existing) {
          if (existing.originalOrder !== index) {
            byIdentity.set(identity, { ...existing, originalOrder: index });
            changed = true;
          }
        } else {
          // Cliente fijo reordenado pero ausente de la plantilla: crear entrada
          byIdentity.set(identity, {
            id: dog.id,
            importKey: dog.importKey,
            name: dog.name,
            originalOrder: index,
            whatsappGroup: dog.whatsappGroup ?? null,
            locationUrl: dog.locationUrl ?? null,
            schedules: Array.isArray(dog.schedules) ? dog.schedules : [],
            count: dog.count ?? 1,
            isFixed: true,
          });
          changed = true;
        }
      });

      if (!changed) return null; // no-op

      changedCount = orderedDogs.length;
      const newDogs = Array.from(byIdentity.values());
      return {
        templateDogs: newDogs,
        windowEndDayKey: current?.windowEndDayKey || null,
        updatedAt: new Date().toISOString(),
        createdFrom: current?.createdFrom || "manual",
      };
    });

    if (changedCount > 0) {
      console.error('[TEMPLATE_ORDER_UPDATE_OK]', JSON.stringify({ profile, count: changedCount }));
    }
  } catch (e) {
    console.error('[TEMPLATE_ORDER_UPDATE_ERR]', e?.message);
  }
};

/**
 * Renombra un perro dentro de la plantilla del perfil, localizándolo por
 * identidad estable (id si existe, si no por nombre normalizado anterior).
 * Sin esto, extendTemplateWindow seguiría inyectando el NOMBRE ANTIGUO en
 * días futuros todavía no creados, resucitando el nombre viejo tras un
 * renombrado.
 *
 * Usa runTransaction por el mismo motivo que el resto de escrituras de
 * plantilla: evitar que un reorder/upsert concurrente pise este rename
 * (o viceversa) por partir de una lectura desactualizada.
 *
 * @param {string} workspaceId
 * @param {string} profile
 * @param {{ id?: string, oldName: string }} identity
 * @param {string} newName
 */
export const renameDogInTemplate = async (workspaceId, profile, identity, newName) => {
  try {
    const oldNameNormalized = (identity?.oldName || "").trim().toUpperCase();
    const targetId = identity?.id;

    let didChange = false;

    await runTemplateTransaction(workspaceId, profile, (current) => {
      if (!current || !Array.isArray(current.templateDogs)) return null;

      let changed = false;
      const newDogs = current.templateDogs.map((d) => {
        const matches = targetId
          ? d.id === targetId
          : (d.name || "").trim().toUpperCase() === oldNameNormalized;
        if (matches) {
          changed = true;
          return { ...d, name: newName };
        }
        return d;
      });

      if (!changed) return null; // no-op

      didChange = true;
      return {
        ...current,
        templateDogs: newDogs,
        updatedAt: new Date().toISOString(),
      };
    });

    if (didChange) {
      console.error('[RENAME_TEMPLATE_OK]', JSON.stringify({ profile, oldName: identity?.oldName, newName, byId: !!targetId }));
    }
  } catch (e) {
    console.error('[RENAME_TEMPLATE_ERR]', e?.message);
  }
};

/**
 * Extiende la ventana de la plantilla hacia el futuro (26 semanas desde hoy)

 * - Lee perros remotos existentes
 * - Hace merge con templateDogs (sin duplicar por name)
 * - Solo escribe si el merge añade algo nuevo
 * - Actualiza windowEndDayKey en la plantilla
 * 
 * @param {string} workspaceId
 * @param {string} profile
 * @returns {Promise<{extended: number, skipped: number}>}
 */
export const extendTemplateWindow = async (workspaceId, profile) => {
  try {
    // 1. Leer plantilla
    const template = await getTemplate(workspaceId, profile);
    if (!template || !template.templateDogs || template.templateDogs.length === 0) {
      console.log('[TEMPLATE_EXTEND_NO_TEMPLATE]', { profile });
      return { extended: 0, skipped: 0 };
    }

    // 2. Calcular ventana objetivo: hoy + 26 semanas
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = formatDateKey(today);
    
    // Calcular 26 semanas desde hoy
    const targetEnd = addWeeksToDateKey(todayKey, 26);
    
    // 3. Si la ventana ya cubre el objetivo, skip
    if (template.windowEndDayKey && template.windowEndDayKey >= targetEnd) {
      console.log('[TEMPLATE_EXTEND_ALREADY_COVERED]', JSON.stringify({
        profile,
        currentEnd: template.windowEndDayKey,
        targetEnd
      }));
      return { extended: 0, skipped: 0 };
    }

    // 4. Generar lista de dayKeys a procesar
    // Desde windowEndDayKey+1 hasta targetEnd
    const startKey = template.windowEndDayKey 
      ? addWeeksToDateKey(template.windowEndDayKey, 0, 1) // +1 día
      : todayKey;
    
    const dayKeysToProcess = [];
    let currentKey = startKey;
    while (currentKey <= targetEnd) {
      dayKeysToProcess.push(currentKey);
      currentKey = addWeeksToDateKey(currentKey, 0, 1); // +1 día
    }

    console.error('[TEMPLATE_EXTEND]', JSON.stringify({
      profile,
      from: startKey,
      to: targetEnd,
      daysCount: dayKeysToProcess.length,
      templateDogsLen: template.templateDogs.length
    }));

    // 5. Procesar cada dayKey: merge + save si cambió
    let extended = 0;
    let skipped = 0;

    for (const dayKey of dayKeysToProcess) {
      try {
        // Leer perros remotos
        const dayDocRef = doc(db, `workspaces/${workspaceId}/days/${dayKey}/profiles/${profile}`);
        const daySnap = await getDoc(dayDocRef);
        
        // 🔒 No recrear docs de perfil que fueron borrados en días pasados o presentes
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayKey = formatDateKey(today);
        if (!daySnap.exists() && dayKey <= todayKey) {
          skipped++;
          continue;
        }
        
        const remoteDogs = daySnap.exists() ? (daySnap.data()?.dogs || []) : [];
        
        // Merge con plantilla por IDENTIDAD (id/importKey/nombre legacy).
        // Remoto tiene prioridad absoluta: nunca se sobrescribe ni duplica.
        // Se pasa dayKey para que cualquier cliente NUEVO materializado en
        // este día resuelva su informativeNote desde recurringNotes (con
        // informativeNoteOverride:false), en vez de nacer con nota vacía
        // sin marcar.
        const merged = mergeByIdentity(remoteDogs, template.templateDogs, dayKey);


        
        // Log del merge
        console.error('[TEMPLATE_EXTEND_MERGE]', JSON.stringify({
          dayKey,
          remoteLen: remoteDogs.length,
          templateLen: template.templateDogs.length,
          mergedLen: merged.length
        }));
        
        // Si el merge no añadió nada nuevo, skip
        if (merged.length === remoteDogs.length) {
          console.error('[TEMPLATE_EXTEND_SKIP]', JSON.stringify({
            dayKey,
            remoteLen: remoteDogs.length
          }));
          skipped++;
          continue;
        }
        
        // Guardar merged list
        await saveDogs(workspaceId, dayKey, merged, profile);
        
        console.error('[TEMPLATE_EXTEND_WRITE_OK]', JSON.stringify({
          dayKey,
          dogsLen: merged.length
        }));
        
        extended++;
      } catch (dayError) {
        console.error('[TEMPLATE_EXTEND_DAY_ERROR]', JSON.stringify({
          dayKey,
          message: dayError?.message || String(dayError)
        }));
      }
    }

    // 6. Actualizar windowEndDayKey en la plantilla
    if (extended > 0) {
      const templateRef = doc(db, `workspaces/${workspaceId}/templates/${profile}`);
      await setDoc(templateRef, {
        ...template,
        windowEndDayKey: targetEnd,
        updatedAt: new Date().toISOString()
      });
      
      console.error('[TEMPLATE_EXTEND_COMPLETE]', JSON.stringify({
        profile,
        extended,
        skipped,
        newWindowEnd: targetEnd
      }));
    }

    return { extended, skipped };
  } catch (e) {
    console.error('[TEMPLATE_EXTEND_ERROR]', JSON.stringify({
      profile,
      message: e?.message || String(e)
    }));
    return { extended: 0, skipped: 0 };
  }
};

/**
 * Hidrata (repara) recurringNotes/informativeNote/informativeNoteOverride de
 * los clientes YA PRESENTES en un día concreto ya materializado en
 * Firestore, comparando contra el histórico autoritativo de la plantilla
 * (templates/{profile}.templateDogs[].recurringNotes). Se invoca al
 * navegar/cargar una fecha (ver AppCore.jsx), no al importar PDF ni al
 * extender la ventana — cubre el caso en que un día ya existente quedó con
 * recurringNotes:[] (por el bug de reimport corregido en dogDataParsers.js,
 * o cualquier otra causa histórica) mientras la plantilla sí tiene la regla
 * correcta.
 *
 * Invariantes estrictos:
 * - NO crea el documento del día si no existe (retorna sin escribir).
 * - NO añade ni elimina ningún cliente del array (solo transforma entradas
 *   ya presentes que tengan coincidencia por identidad con la plantilla).
 * - Preserva EXACTAMENTE todos los demás campos de cada perro (orden,
 *   id/importKey/name, schedules, whatsappGroup, locationUrl, count,
 *   isFixed, y cualquier otro campo no mencionado aquí).
 * - Modifica EXCLUSIVAMENTE recurringNotes / informativeNote /
 *   informativeNoteOverride, y solo cuando corresponde según estos casos:
 *
 *   A. Excepción puntual protegida (isProtectedNoteException === true):
 *      se conserva informativeNote/informativeNoteOverride EXACTOS, solo
 *      se sincroniza recurringNotes desde la plantilla.
 *   B. Registro vacío inequívoco (nota vacía, override false/ausente,
 *      historial vacío/ausente): se copia recurringNotes de la plantilla,
 *      se recalcula informativeNote para este dayKey, override:false.
 *   C. Registro con historial recurrente propio no vacío (posiblemente
 *      desactualizado): se sincroniza igual que el caso B.
 *   D. Nota legacy ambigua (nota no vacía, override false/ausente,
 *      historial vacío/ausente): NO SE TOCA NINGÚN CAMPO.
 *
 * Guard anti-corrupción: si la plantilla tiene recurringNotes:[] pero el
 * registro del día YA tiene un historial no vacío, ese estado es
 * SOSPECHOSO (terminar una recurrencia legítimamente SIEMPRE deja al menos
 * una versión cerrada con endDateKey en el array — nunca lo vacía, ver
 * reviseRecurringNotes en noteRules.js). En ese caso se preserva el
 * registro existente TAL CUAL, sin tocar ningún campo, para no destruir un
 * historial válido por una plantilla corrupta o parcialmente reimportada.
 *
 * Transaccional: lecturas SECUENCIALES (no Promise.all) de templateRef y
 * dayRef dentro de la misma runTransaction, con reintento automático de
 * Firestore ante conflicto de versión concurrente (mismo patrón que
 * runTemplateTransaction). Solo escribe (tx.set) si al menos un cliente
 * cambió; si no, la transacción termina sin ninguna escritura (idempotente).
 *
 * No captura su propio error: se propaga al llamador (AppCore.jsx), que es
 * el único punto de gestión/log de error para evitar doble captura.
 *
 * @param {string} workspaceId
 * @param {string} profileId
 * @param {string} dayKey - "YYYY-MM-DD"
 * @returns {Promise<{hydrated: boolean, reason?: string}>}
 */
export const hydrateRecurringNotesForExistingDay = async (workspaceId, profileId, dayKey) => {
  if (!workspaceId || !profileId || !dayKey) {
    return { hydrated: false, reason: "missing-params" };
  }

  const templateRef = doc(db, `workspaces/${workspaceId}/templates/${profileId}`);
  const dayRef = doc(db, `workspaces/${workspaceId}/days/${dayKey}/profiles/${profileId}`);

  const result = await runTransaction(db, async (tx) => {
    // Lecturas SECUENCIALES dentro de la transacción (no Promise.all): la
    // documentación del SDK modular solo garantiza este patrón para el
    // tracking interno de documentos leídos por runTransaction.
    const templateSnap = await tx.get(templateRef);
    const daySnap = await tx.get(dayRef);

    // Día inexistente: no crear nada, no escribir nada.
    if (!daySnap.exists()) return { changed: false, reason: "day-not-exists" };

    const templateDogs = Array.isArray(templateSnap.data()?.templateDogs)
      ? templateSnap.data().templateDogs
      : [];
    if (templateDogs.length === 0) return { changed: false, reason: "no-template" };

    const remoteDogs = Array.isArray(daySnap.data()?.dogs) ? daySnap.data().dogs : [];
    if (remoteDogs.length === 0) return { changed: false, reason: "day-empty" };

    const templateByIdentity = new Map(templateDogs.map(d => [getDogIdentity(d), d]));
    let anyChanged = false;

    const nextDogs = remoteDogs.map((existing) => {
      const templateEntry = templateByIdentity.get(getDogIdentity(existing));
      if (!templateEntry) return existing; // sin match en plantilla: no tocar

      const templateHistory = Array.isArray(templateEntry.recurringNotes)
        ? templateEntry.recurringNotes
        : [];
      const existingHistory = Array.isArray(existing.recurringNotes)
        ? existing.recurringNotes
        : [];

      // --- notePatch: EXACTAMENTE la misma semántica anterior (casos A-D) ---
      // Guard anti-corrupción: plantilla vacía + historial existente no
      // vacío es sospechoso (nunca resultado de una terminación legítima,
      // que siempre deja al menos un registro cerrado con endDateKey).
      // Este guard bloquea ÚNICAMENTE notePatch — nunca debe impedir que
      // schedulesPatch (calculado más abajo, fuera de este bloque) se
      // evalúe con independencia del estado de recurringNotes.
      const mayHydrateNotes = !(templateHistory.length === 0 && existingHistory.length > 0);

      let notePatch = {};
      if (mayHydrateNotes) {
        // Caso A: excepción puntual protegida — nunca tocar note/override.
        if (isProtectedNoteException(existing)) {
          const sameHistory = JSON.stringify(existingHistory) === JSON.stringify(templateHistory);
          if (!sameHistory) {
            notePatch = { recurringNotes: templateHistory };
          }
        } else {
          const noteIsEmpty = !existing.informativeNote || existing.informativeNote === "";
          const hasOwnHistory = existingHistory.length > 0;

          // Casos B y C: vacío inequívoco, o historial propio a sincronizar.
          if (noteIsEmpty || hasOwnHistory) {
            const resolvedNote = resolveNoteForDate(templateHistory, dayKey);
            const historyChanged = JSON.stringify(existingHistory) !== JSON.stringify(templateHistory);
            const noteChanged = (existing.informativeNote || "") !== resolvedNote;
            const overrideChanged = !!existing.informativeNoteOverride !== false;
            if (historyChanged || noteChanged || overrideChanged) {
              notePatch = {
                recurringNotes: templateHistory,
                informativeNote: resolvedNote,
                informativeNoteOverride: false,
              };
            }
          }
          // Caso D: nota legacy ambigua — notePatch queda {} (no tocar nada).
        }
      }

      // --- schedulesPatch: SIEMPRE se calcula, independiente de notePatch ---
      // Si la plantilla no tiene schedules recurrentes, NUNCA se toca
      // existing.schedules (ni se borra ni se sustituye por []): se
      // conserva íntegro (recurrentes + puntuales) tal cual estaba.
      // Si la plantilla SÍ tiene schedules recurrentes, se reemplazan
      // únicamente los recurrentes existentes por los de la plantilla,
      // conservando intactos los puntuales (onlyThisWeek === true) del
      // propio día. Comparación por contenido (JSON.stringify) antes de
      // incluir el campo en el patch, para idempotencia real.
      const templateSchedules = Array.isArray(templateEntry.schedules)
        ? templateEntry.schedules.filter(s => !(s.onlyThisWeek ?? false))
        : [];

      let schedulesPatch = {};
      if (templateSchedules.length > 0) {
        const existingPunctual = Array.isArray(existing.schedules)
          ? existing.schedules.filter(s => (s.onlyThisWeek ?? false))
          : [];
        const mergedSchedules = [...templateSchedules, ...existingPunctual];
        if (JSON.stringify(existing.schedules || []) !== JSON.stringify(mergedSchedules)) {
          schedulesPatch = { schedules: mergedSchedules };
        }
      }

      if (Object.keys(notePatch).length === 0 && Object.keys(schedulesPatch).length === 0) {
        return existing;
      }

      anyChanged = true;
      return { ...existing, ...notePatch, ...schedulesPatch };
    });


    if (!anyChanged) return { changed: false, reason: "no-changes" }; // idempotente

    const sanitized = sanitizeForFirestore(nextDogs);
    tx.set(
      dayRef,
      { dogs: sanitized, count: sanitized.length, _updatedAt: serverTimestamp() },
      { merge: true }
    );
    return { changed: true, reason: "hydrated" };
  });

  return { hydrated: !!result?.changed, reason: result?.reason };
};
